<script setup lang="ts">
import type StrengthBar from '@/components/auth/StrengthBar.vue';
import IconWrapper from '@/components/base/IconWrapper.vue';

const strengthBarRef = ref<InstanceType<typeof StrengthBar> | null>(null);
const { registerData, validationData } = storeToRefs(useRegisterStore());
const { allCountriesList } = storeToRefs(useCountriesStore());
const { referenceCode } = useParameters();

const { isSocialAuthFlow } = useAuthSocial();
const { isRegisterRequestPhone } = useAuth();

const formValueAffiliationCode = ref<string>(referenceCode.value);
const ddiError = ref<string>('');

const selectedDdi = ref({
  id: 4,
  name: 'Brasil',
  code: 'BRA',
  ddi: '+55',
  currency: 'BRL',
  alpha2: 'BR',
});

const maskByCountryPhone = computed<string[] | string>(() => {
  if (!selectedDdi.value.code) return '';

  // @ts-ignore
  return phoneMaksInAlpha3[selectedDdi.value.code] ?? '';
});

const countriesSortDdi = computed(() => {
  if (!allCountriesList?.value) {
    return;
  }

  return allCountriesList.value.sort((a: any, b: any) => a.ddi - b.ddi);
});

function updatePasswordStrength() {
  strengthBarRef.value?.updatePasswordStrength();
}

function countMaskLength(mask: any) {
  if (!Array.isArray(mask) || mask.length === 0) {
    return 0;
  }

  return mask.reduce((min, item) => Math.min(min, item.length), Infinity);
}

function onKeyupPhone() {
  validationData.value.fields.ddi = registerData.value.ddi.length >= 2;
  validationData.value.fields.phone =
    registerData.value.phone.length >=
    countMaskLength(maskByCountryPhone.value);
}

watch(
  () => selectedDdi.value.ddi,
  () => {
    registerData.value.ddi = selectedDdi.value.ddi;
  },
  { immediate: true },
);
</script>

<template>
  <form class="registerFields">
    <template v-if="isRegisterRequestPhone">
      <label
        class="registerLabel"
        for="phone"
      >
        {{ $t('auth.register.phone') }} <span>*</span>
      </label>

      <div>
        <BaseFormInput
          id="phone"
          v-model="registerData.phone"
          autocomplete="off"
          :data-maska="maskByCountryPhone"
          :info="$t('multiStepRegister.phone_info')"
          name="phone"
          placeholder="ex: (31) 99876-5432"
          :required="true"
          type="tel"
          @keyup="onKeyupPhone"
        >
          <template #prepend>
            <HeadlessListbox
              v-model="selectedDdi"
              as="div"
              :class="$style.ddiSelect"
            >
              <HeadlessListboxButton
                :class="$style.ddiBtn"
                @click="ddiError = ''"
              >
                <IconWrapper
                  v-if="selectedDdi.code"
                  class="text-xl mr-2"
                  :name="`flags/${selectedDdi.code}`"
                />
                {{ selectedDdi.ddi || 'DDI' }}
                <NuxtIcon
                  v-if="!selectedDdi.code"
                  class="ml-3 opacity-30"
                  name="arrows/angle_down"
                />
              </HeadlessListboxButton>

              <HeadlessListboxOptions :class="$style.ddiOptionsGroup">
                <div class="px-1 py-1 relative overflow-auto h-full">
                  <HeadlessListboxOption
                    v-for="option in countriesSortDdi"
                    :key="option.id"
                    :value="option"
                  >
                    <span :class="[$style.ddiOption, 'group']">
                      <IconWrapper
                        class="text-xl mr-2"
                        :name="`flags/${option.code}`"
                      />
                      {{ option.ddi }}
                    </span>
                  </HeadlessListboxOption>
                </div>
              </HeadlessListboxOptions>
            </HeadlessListbox>
          </template>
        </BaseFormInput>
      </div>
    </template>

    <template v-if="!isSocialAuthFlow">
      <label
        class="registerLabel"
        for="password"
      >
        {{ $t('auth.register.password') }} <span>*</span>
      </label>

      <BaseFormInput
        id="password"
        v-model="registerData.password"
        autocomplete="password"
        name="password"
        placeholder="crie uma senha segura..."
        :required="true"
        :show-hide-pass="true"
        @keyup="updatePasswordStrength"
      />

      <AuthStrengthBar
        ref="strengthBarRef"
        :password="registerData.password"
      />
    </template>

    <div v-if="formValueAffiliationCode">
      <label
        class="block mb-1 registerLabel"
        for="affiliation_code"
      >
        {{ $t('auth.register.reference_code') }}
      </label>
      <BaseFormInput
        id="affiliation_code"
        autocomplete="off"
        disabled
        :initial-value="formValueAffiliationCode"
        :model-value="formValueAffiliationCode"
        name="affiliation_code"
        :readonly="!!formValueAffiliationCode"
        :required="false"
        type="text"
        :with-check="true"
      />
    </div>
  </form>
</template>

<style module lang="scss">
.ddiSelect {
  @apply relative inline-block text-left text-texts border-solid border-r border-auth-text-inputs/20;
  .ddiBtn {
    @apply inline-flex w-full justify-center items-center rounded-none px-4 py-2 text-sm font-medium;
  }
  .ddiOptionsGroup {
    @apply absolute left-0 mt-2 w-24 origin-top-right divide-y divide-white rounded-md bg-auth-bg-inputs shadow-lg ring-1 ring-black/5 focus:outline-none z-[999] overflow-hidden h-[200px];
  }
  .ddiOption {
    @apply flex w-full items-center rounded-md px-2 py-2 text-sm cursor-pointer;
  }
}
.refCodeButton {
  @apply text-auth-texts text-xs font-medium mt-2;
}
</style>
